import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { use100vh } from 'react-div-100vh';
import { selectZipcode } from 'redux/slices/zipcodeSlice';
import { pageLoad } from 'helpers/Analytics';
import { displayZipgate } from 'helpers/DisplayZipgate';
import {
  IRAQConfiguration,
  IFormData,
  IRequestAQuoteData,
  VehicleSelectorType,
  IRAQConfigurationBAP,
  IRAQConfigurationInventory,
  IADXEnhancedData,
} from 'interfaces/requestAQuote';
import { IPageLoadData } from 'interfaces/analytics';
import ModalContainer from 'components/Modal/ModalContainer/ModalContainer';
import { useStyles } from './RequestAQuoteModal.styles';
import {
  getCurrentConfig,
  setConfigStatus,
  setCurrentConfig,
} from 'redux/slices/savedConfigSlice';
import { generateParameters } from 'helpers/Url';
import { useLoadRAQData } from 'hooks/useLoadRAQData';
import { ISavedConfigModalContent } from 'interfaces/modal';
import RequestAQuoteContainer from 'components/RequestAQuote/RequestAQuoteContainer/RequestAQuoteContainer';
import RequestAQuoteSkeleton from 'components/RequestAQuote/RequestAQuoteSkeleton/RequestAQuoteSkeleton';
import SavedConfigModal from '../SavedConfigModal/SavedConfigModal';

export enum FormStatusOptions {
  initial = 'inital',
  success = 'success',
  error = 'error',
}

export interface IRequestAQuote {
  configuration?: IRAQConfiguration;
  data?: IRequestAQuoteData;
  isInModal?: boolean;
  title?: string;
  description?: string;
  scrollContainerId?: string;
  shoppingTool?: string;
  hideChangeDealer?: boolean;
  pageLoadConfig?: IPageLoadData;
  confirmationPageLoadConfig?: IPageLoadData;
  setFormData?: (formData: IFormData) => void;
  setFormStatus?: (formStatus: FormStatusOptions) => void;
}

export interface IADXEnhanced {
  configuration?: IRAQConfiguration;
  data?: IADXEnhancedData;
  isInModal?: boolean;
  title?: string;
  description?: string;
  scrollContainerId?: string;
  shoppingTool?: string;
  hideChangeDealer?: boolean;
  pageLoadConfig?: IPageLoadData;
  confirmationPageLoadConfig?: IPageLoadData;
  setFormData?: (formData: IFormData) => void;
  setFormStatus?: (formStatus: FormStatusOptions) => void;
}

export interface IRequestAQuoteModalProps {
  closeFunction: () => void;
  isOpen: boolean;
  configuration?: IRAQConfiguration;
  raqTitle?: string;
  raqDescription?: string;
  source?: string;
  hideChangeDealer?: boolean;
  pageLoadConfig?: IPageLoadData;
  confirmationPageLoadConfig?: IPageLoadData;
  zipcodeTest?: string; //Use for testing
  setFormData?: (formData: IFormData) => void;
}

export function RequestAQuoteModal({
  closeFunction,
  isOpen,
  configuration,
  raqTitle,
  raqDescription,
  source,
  hideChangeDealer,
  pageLoadConfig,
  confirmationPageLoadConfig,
  zipcodeTest,
  setFormData,
}: IRequestAQuoteModalProps): JSX.Element {
  const viewHeightValue = use100vh();
  const viewHeightStyle = viewHeightValue
    ? `${viewHeightValue / 16}rem`
    : '100%';
  const classes = useStyles({ viewHeight: viewHeightStyle });
  const requestAQuoteModalTitle = 'request a quote';
  const modalId = 'raq-modal';
  const zipcode = useSelector(selectZipcode);

  const dispatch = useDispatch();
  const { currentValue, configStatus } = useSelector(getCurrentConfig);
  const isSavedConfigModalOpen = !!currentValue && configStatus === undefined;
  const isConfigFromSaved =
    !!currentValue &&
    configStatus &&
    configuration?.variant !== VehicleSelectorType.bap &&
    configuration?.variant !== VehicleSelectorType.inventory;
  const raqEndpoint = `/platform/api/v1/request-a-quote`;
  let raqEndpointParams = '';
  if (isConfigFromSaved) {
    raqEndpointParams = generateParameters({
      modelId: currentValue?.modelId,
      exteriorColorCode: currentValue?.exterior,
      interiorColorCode: currentValue?.interior,
      fullTaxonomy: 'true',
      zipCode: zipcode,
    });
  } else if (configuration && configuration.modelId) {
    const { modelId, exteriorColor, interiorColor } =
      configuration as IRAQConfigurationBAP;
    const { dealerId } = configuration as IRAQConfigurationInventory;
    raqEndpointParams = generateParameters({
      modelId,
      fullTaxonomy: 'true',
      exteriorColorCode: exteriorColor?.internalCode,
      interiorColorCode: interiorColor?.internalCode,
      dealerId,
      zipCode: zipcode,
    });
  }

  const { status, data } = useLoadRAQData(`${raqEndpoint}${raqEndpointParams}`);

  const { pageContent, vehicleData } = data || {};
  const { modals } = pageContent || {};
  const { raqModalSavedConfig } = modals || {};
  const savedConfigContent = raqModalSavedConfig as ISavedConfigModalContent;

  const finalConfiguration =
    isConfigFromSaved && vehicleData
      ? {
          modelId: vehicleData.modelId.toUpperCase(),
          modelSeries: vehicleData.modelSeries.toUpperCase(),
          modelYear: vehicleData.modelYear.toString(),
          exteriorColor: vehicleData.exteriorColor,
          interiorColor: vehicleData.interiorColor,
          accessories: currentValue?.accessories,
          powertrain: vehicleData.engine,
          drivetrain: vehicleData.drivetrain,
          package: '',
          msrp: currentValue?.totalPrice,
          variant: VehicleSelectorType.savedConfig,
          vinNumber: configuration?.vinNumber,
        }
      : configuration;

  const handleSavedConfigContinue = () => {
    dispatch(setConfigStatus(true));
  };

  const handleSavedConfigCancel = () => {
    dispatch(setConfigStatus(false));
  };

  useEffect(() => {
    if (pageLoadConfig) {
      pageLoad(pageLoadConfig);
    }
    !zipcode && displayZipgate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const { modelSeries, modelYear } = configuration || {};
    if (modelSeries && modelYear) {
      const key = `${modelSeries.toLowerCase()}-${modelYear}`;
      dispatch(setCurrentConfig(key));

      if (source === 'payest') {
        handleSavedConfigCancel();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestAQuoteLoader = () => {
    switch (status) {
      case 'error':
        return <span className="error">{status}</span>;
      case 'success':
        return (
          <>
            <RequestAQuoteContainer
              title={raqTitle}
              description={raqDescription}
              scrollContainerId={modalId}
              shoppingTool={source}
              confirmationPageLoadConfig={confirmationPageLoadConfig}
              setFormData={setFormData}
              configuration={finalConfiguration}
              data={data as IRequestAQuoteData}
              hideChangeDealer={hideChangeDealer}
              isInModal={true}
            />
            <SavedConfigModal
              referrerPage="RAQ"
              cancelFunction={handleSavedConfigCancel}
              continueFunction={handleSavedConfigContinue}
              isOpen={isSavedConfigModalOpen}
              data={savedConfigContent}
              closePageLoadTracking={pageLoadConfig}
            />
          </>
        );
      default:
        return (
          <RequestAQuoteSkeleton
            title={raqTitle || ''}
            description={raqDescription || ''}
            configuration={configuration}
          />
        );
    }
  };

  return (
    <>
      {(zipcodeTest !== undefined || zipcode) && (
        <ModalContainer
          closeFunction={closeFunction}
          isOpen={isOpen}
          className={classes.paper}
          title={requestAQuoteModalTitle}
          displayTitle={false}
          modalId={modalId}
          noTracking={true}
        >
          {requestAQuoteLoader()}
        </ModalContainer>
      )}
    </>
  );
}
